import { useState } from 'react';
import { Plus } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ReturnForm } from '@/components/returns/ReturnForm';
import { ReturnList } from '@/components/returns/ReturnList';
import { useRecentReturns, useReturnsByEvent } from '@/hooks/useReturns';
import { useActiveEvent } from '@/hooks/useActiveEvent';
import { formatIDR, formatNumber } from '@/lib/format';

export default function Returns() {
  const { activeEventId, activeEvent } = useActiveEvent();
  const { data: eventReturns, isLoading: loadingEvent } = useReturnsByEvent(activeEventId);
  const { data: recent, isLoading: loadingRecent } = useRecentReturns(20);
  const [formOpen, setFormOpen] = useState(false);

  // Prefer event-scoped, fall back to recent
  const data = activeEventId ? eventReturns : recent;
  const isLoading = activeEventId ? loadingEvent : loadingRecent;

  const totalRefund = (data ?? []).reduce((sum, r) => sum + r.refundAmount, 0);
  const totalItems = (data ?? []).reduce((sum, r) => sum + r.items.length, 0);

  return (
    <>
      <PageHeader
        overline={activeEvent ? `Event: ${activeEvent.name}` : 'All events'}
        title="Returns"
        description="Pengembalian produk (partial per item) — stock auto-restock & invoice status update."
        actions={
          <Button
            variant="primary"
            leftIcon={<Plus size={16} />}
            onClick={() => setFormOpen(true)}
            disabled={!activeEventId}
          >
            New return
          </Button>
        }
      />

      {!activeEventId && (
        <Card className="mb-6 border-warning/40 bg-warning/5">
          <p className="text-small text-warning">
            ⚠ Pilih event aktif dulu di TopBar. Return harus terikat ke event untuk laporan.
          </p>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-3 mb-6">
        <Card padding="md">
          <p className="text-overline uppercase text-ink-subtle font-medium">Total return</p>
          <p className="text-metric font-display font-semibold mt-2 text-ink">
            {formatNumber(data?.length ?? 0)}
          </p>
        </Card>
        <Card padding="md">
          <p className="text-overline uppercase text-ink-subtle font-medium">Item dikembalikan</p>
          <p className="text-metric font-display font-semibold mt-2 text-ink">
            {formatNumber(totalItems)}
          </p>
        </Card>
        <Card padding="md">
          <p className="text-overline uppercase text-ink-subtle font-medium">Total refund</p>
          <p className="text-metric font-display font-semibold mt-2 text-ink">
            {formatIDR(totalRefund)}
          </p>
        </Card>
      </div>

      <Card padding="none">
        <ReturnList returns={data ?? []} isLoading={isLoading} />
      </Card>

      <ReturnForm open={formOpen} onClose={() => setFormOpen(false)} />
    </>
  );
}
